import { getField, updateField } from "vuex-map-fields";
import { VuexModule, Module, Action } from "vuex-module-decorators";
import SummaryService from "@/views/summary/summary.service";
import { 
  AlertZone, 
  AssessmentTypes,
  PatientSummary ,
  Utox,
  SurveyGroup,
  SurveyGroupParticipant,
  ParticipantSynopsisResponse,
  ParticipantSynopsisRequest
} from "@/views/summary/summary.types";

@Module({ namespaced: true })
class Summary extends VuexModule {
  phq9AlertZones: AlertZone[] = [];
  gad7AlertZones: AlertZone[] = [];
  sowsAlertZones: AlertZone[] = [];
  patientSummaries: PatientSummary[] = [];
  surveyGroups: SurveyGroup[] = [];
  participantSynopsisResponse = new ParticipantSynopsisResponse();
  utoxIcon = new Utox();
  selectedPatientSummary = new PatientSummary();

  sortBy = '';
  sortDesc = false;
  searchQuery = '';

  private _initialState!: Record<string, any>;

  constructor(module: any) {
    super(module);
    this._initialState = { ...this };
  }

  @Action({ rawError: true })
  async fetchPhq9ChartOptions(clientId: number) {
    const phq9AlertZones = await SummaryService.getAlertZones(AssessmentTypes.PHQ9, clientId);
    this.context.commit("updateField", {
      path: "phq9AlertZones",
      value: phq9AlertZones
    });
  }

  @Action({ rawError: true })
  async fetchGad7ChartOptions(clientId: number) {
    const gad7AlertZones = await SummaryService.getAlertZones(AssessmentTypes.GAD7, clientId);
    this.context.commit("updateField", {
      path: "gad7AlertZones",
      value: gad7AlertZones
    });
  }

  @Action({ rawError: true })
  async fetchSowsChartOptions(clientId: number) {
    const sowsAlertZones = await SummaryService.getAlertZones(AssessmentTypes.SOWS, clientId);
    this.context.commit("updateField", {
      path: "sowsAlertZones",
      value: sowsAlertZones
    });
  }

  @Action({ rawError: true })
  async fetchPatientSummary({ clientId, show, timezone } : { clientId: number, show: boolean, timezone: string }) {
    const response = await SummaryService.getPatientSummary(clientId, show, timezone);
    const patientSummaries = response.map(item => ({ ...item }));
    this.context.commit("updateField", {
      path: "patientSummaries",
      value: patientSummaries
    });
  }

  @Action({ rawError: true })
  async fetchUtoxById({ startDate, participantId, clientId } : { startDate: string, participantId: number, clientId: number }) {
    const utoxIcon = await SummaryService.getUtoxById(startDate, participantId, clientId);
    this.context.commit("updateField", {
      path: "utoxIcon",
      value: utoxIcon
    });
  }

  @Action({ rawError: true })
  async fetchSurveyGroups(clientId: number) {
    const surveyGroups = await SummaryService.getSurveyGroups(clientId);
    this.context.commit("updateField", {
      path: "surveyGroups",
      value: surveyGroups
    });
  }

  @Action({ rawError: true })
  async addSurveyGroupParticipantMap(surveyGroupParticipant: SurveyGroupParticipant) {
    try {
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: null
      }, { root: true });
      await SummaryService.AddSurveyGroupParticipantMap(surveyGroupParticipant);
    } catch (e: any) {
      const errorMessage = e.response?.data || e.response.data.message || "An unexpected error occurred.";
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: errorMessage
      }, { root: true });
    }
  }

  @Action({ rawError: true })
  async updateParticipantShow({ clientId, participantId, show } : {clientId: number, participantId: number, show: boolean }) {
    try {
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: null
      }, { root: true });
      await SummaryService.UpdateParticipantShow(clientId, participantId, show);
    } catch (e: any) {
      const errorMessage = e.response?.data || e.response.data.message || "An unexpected error occurred.";
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: errorMessage
      }, { root: true });
    }
  }

  @Action({ rawError: true })
  async fetchAiInsight(participantSynopsisRequest: ParticipantSynopsisRequest) {
    this.context.commit("updateField", {
      path: "participantSynopsisResponse",
      value: new ParticipantSynopsisResponse()
    });
    const participantSynopsisResponse = await SummaryService.getAiInsight(participantSynopsisRequest);
    this.context.commit("updateField", {
      path: "participantSynopsisResponse",
      value: participantSynopsisResponse
    });
  }

  @Action({ rawError: true })
  resetState() {
    for (const key of Object.keys(this._initialState)) {
      this.context.commit("updateField", {
        path: key,
        value: this._initialState[key]
      });
    }
  }
}

Summary.getters = { getField };
Summary.mutations = { updateField };

export default Summary;

